import { MouseEvent, useLayoutEffect } from "react"
import { useAuthContext } from "../../features/auth/AuthProvider"
import { homeRoute, templatesRoute, useRouteContext } from "../../features/route/RouteProvider"
import { TEMPLATES } from "../../models/template"
import { TemplateBox } from "./TemplateBox"
import { TEMPLATE_CONTENT } from "./content"

type TemplateDetailViewProps = {
  templateId: string
  /** Origin hint from the URL — determines back-link text and target. */
  from?: "templates"
}

export function TemplateDetailView({ templateId, from }: TemplateDetailViewProps) {
  const template = TEMPLATES.find((t) => t.id === templateId)
  const [uid] = useAuthContext()
  const [, setRoute] = useRouteContext()

  // Snap the viewport to the top on nav-in (or on template change) so the hero
  // card lands where the user expects. useLayoutEffect fires inside the
  // startViewTransition flushSync window — the new snapshot captures the
  // scrolled-to-top state, so the shared-element morph ends at the right
  // position instead of appearing off-screen when the user clicked a card
  // that was scrolled down in the grid.
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [templateId])

  if (!template) {
    // Route parser validates against KNOWN_TEMPLATE_IDS, so this branch is only
    // reachable if the two lists drift out of sync. Render a minimal fallback
    // rather than crash.
    return (
      <section className="section">
        <div className="container">
          <p>Template not found. <a href="/">Back to home</a></p>
        </div>
      </section>
    )
  }

  const cameFromTemplates = from === "templates"
  const backLabel = cameFromTemplates ? "Templates" : "Home"
  const handleBackClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    setRoute(cameFromTemplates ? templatesRoute : homeRoute)
  }
  const backHref = cameFromTemplates ? "/templates" : "/"

  const Content = TEMPLATE_CONTENT[template.id]

  return (
    <section className="section">
      <div className="container">
        <div className="mb-4">
          <a href={backHref} onClick={handleBackClick} className="button is-ghost has-text-info">
            <span className="icon">
              <i className="icon-left-open" aria-hidden="true"></i>
            </span>
            <span>{backLabel}</span>
          </a>
        </div>

        <div className="template-detail__hero">
          <TemplateBox template={template} userUid={uid ?? undefined} hideInfoPill={true} />
        </div>

        <div className="content mt-5 template-detail__article">
          {Content
            ? <Content />
            : <p><em>Content for this template is still being written.</em></p>}

          <h2>Column prompts</h2>
          <p>Each column asks participants a specific question. Retroboard sets these up for you:</p>
          <ul>
            {template.columns.map((col) => (
              <li key={col.title}>
                <strong>{col.title}</strong>
                {col.description ? <> &mdash; {col.description}</> : null}
              </li>
            ))}
          </ul>

          <h2>Related formats</h2>
          <p>
            <a
              href="/templates"
              onClick={(e) => {
                e.preventDefault()
                setRoute(templatesRoute)
              }}
            >
              See all templates
            </a>
          </p>
        </div>
      </div>
    </section>
  )
}
